// OutstandingWork — single page that surfaces every item across DCVFD's
// internal stack that needs human attention: failed sync runs, LLM call
// failures, active alerts, applicant pipeline stalls. Sorted by severity
// then age. Click any item to drill into its source-of-truth.
//
// Sources covered today: sync_runs, llm_calls, dash_ai_alert_events,
// dash_applicants. Plane (open issues) + GitHub (open PRs/issues) land in
// follow-up PRs once the backend has those creds.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/shared/PageHeader";
import Banner from "../components/shared/Banner";

type Severity = "urgent" | "high" | "medium" | "low";
type Source = "sync" | "llm" | "alerts" | "pipeline";

interface WorkItem {
  id: string;
  source: Source;
  severity: Severity;
  title: string;
  summary?: string;
  link?: string;
  age_hours?: number;
  surface?: string;
}

interface OutstandingFeed {
  items: WorkItem[];
  counts: {
    total: number;
    by_source: Record<Source, number>;
    by_severity: Record<Severity, number>;
  };
  generated_at: string;
}

const SEVERITY_BADGE: Record<Severity, { label: string; cls: string }> = {
  urgent: {
    label: "Urgent",
    cls: "bg-red-500/15 text-red-300 border-red-500/30",
  },
  high: {
    label: "High",
    cls: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  },
  medium: {
    label: "Medium",
    cls: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  },
  low: { label: "Low", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
};

const SOURCE_LABEL: Record<Source, string> = {
  sync: "Sync runs",
  llm: "LLM calls",
  alerts: "AI alerts",
  pipeline: "Pipeline",
};

function ageLabel(hours?: number): string {
  if (hours === undefined || hours === null) return "";
  if (hours < 1) return "just now";
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export default function OutstandingWork() {
  const [data, setData] = useState<OutstandingFeed | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filterSource, setFilterSource] = useState<Source | "all">("all");
  const [filterSeverity, setFilterSeverity] = useState<Severity | "all">("all");

  useEffect(() => {
    let cancelled = false;
    fetch("/api/outstanding-work", { credentials: "same-origin" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as OutstandingFeed;
      })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setErr(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const SEVERITY_RANK: Record<Severity, number> = {
    urgent: 0,
    high: 1,
    medium: 2,
    low: 3,
  };
  const items = (data?.items ?? [])
    .filter((it) => {
      if (filterSource !== "all" && it.source !== filterSource) return false;
      if (filterSeverity !== "all" && it.severity !== filterSeverity)
        return false;
      return true;
    })
    // Enforce the "sorted by severity, then age" contract the header promises,
    // rather than trusting whatever order the API returned.
    .sort((a, b) => {
      const s = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
      if (s !== 0) return s;
      return (b.age_hours ?? 0) - (a.age_hours ?? 0);
    });

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <PageHeader
          title="Outstanding Work"
          subtitle={
            <>
              Cross-source feed of items that need human attention. Sorted by
              severity, then age.
              {data?.generated_at && (
                <span className="block text-xs text-theme-muted mt-1 font-mono">
                  generated {new Date(data.generated_at).toLocaleString()}
                </span>
              )}
            </>
          }
        />
      </div>

      {/* Counts strip */}
      {data && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => setFilterSeverity("all")}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${
              filterSeverity === "all"
                ? "bg-theme-primary/20 text-theme-primary border-theme-primary/40"
                : "border-theme-subtle text-theme-muted hover:text-theme-primary"
            }`}
          >
            All <span className="font-mono">({data.counts.total})</span>
          </button>
          {(["urgent", "high", "medium", "low"] as Severity[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilterSeverity(s)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filterSeverity === s
                  ? SEVERITY_BADGE[s].cls
                  : "border-theme-subtle text-theme-muted hover:text-theme-primary"
              }`}
            >
              {SEVERITY_BADGE[s].label}{" "}
              <span className="font-mono">({data.counts.by_severity[s]})</span>
            </button>
          ))}
        </div>
      )}

      {/* Source chips */}
      {data && (
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            type="button"
            onClick={() => setFilterSource("all")}
            className={`text-xs px-3 py-1.5 rounded-md border transition ${
              filterSource === "all"
                ? "bg-theme-overlay border-theme-default text-theme-primary"
                : "border-theme-subtle text-theme-muted hover:text-theme-primary"
            }`}
          >
            All sources
          </button>
          {(["sync", "llm", "alerts", "pipeline"] as Source[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilterSource(s)}
              className={`text-xs px-3 py-1.5 rounded-md border transition ${
                filterSource === s
                  ? "bg-theme-overlay border-theme-default text-theme-primary"
                  : "border-theme-subtle text-theme-muted hover:text-theme-primary"
              }`}
            >
              {SOURCE_LABEL[s]}{" "}
              <span className="font-mono">({data.counts.by_source[s]})</span>
            </button>
          ))}
        </div>
      )}

      {err && (
        <Banner variant="error" className="mb-4">
          Failed to load: {err}
        </Banner>
      )}

      {!err && !data && (
        <div className="text-sm text-theme-muted">Loading…</div>
      )}

      {data && items.length === 0 && (
        <div className="rounded-xl border border-theme-subtle p-8 text-center">
          <p className="text-sm text-theme-muted">
            {data.counts.total === 0
              ? "No outstanding items right now. Everything is healthy."
              : "No items match the current filters."}
          </p>
        </div>
      )}

      {/* Item list */}
      <ul className="space-y-2">
        {items.map((it) => {
          const sev = SEVERITY_BADGE[it.severity];
          return (
            <li
              key={it.id}
              className="rounded-lg border border-theme-subtle hover:bg-theme-overlay/40 transition"
            >
              <ItemBody item={it} sev={sev} />
            </li>
          );
        })}
      </ul>

      <p className="text-xs text-theme-muted mt-6">
        Plane open issues + GitHub PRs land in a follow-up. See DATA-71 for the
        roadmap.
      </p>
    </div>
  );
}

function ItemBody({
  item,
  sev,
}: {
  item: WorkItem;
  sev: { label: string; cls: string };
}) {
  const inner = (
    <div className="flex items-start gap-3 p-3">
      <span
        className={`text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full border shrink-0 ${sev.cls}`}
      >
        {sev.label}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-theme-primary truncate">
          {item.title}
        </p>
        {item.summary && (
          <p className="text-xs text-theme-muted mt-1 line-clamp-2 font-mono">
            {item.summary}
          </p>
        )}
        <p className="text-xs text-theme-muted mt-1">
          <span className="uppercase tracking-wider">
            {SOURCE_LABEL[item.source]}
          </span>
          {item.surface && (
            <span className="font-mono ml-2">{item.surface}</span>
          )}
          {item.age_hours !== undefined && (
            <span className="ml-2">{ageLabel(item.age_hours)}</span>
          )}
        </p>
      </div>
    </div>
  );
  // Internal links (start with `/`) go through the SPA router; external links open new tab.
  if (!item.link) return <div>{inner}</div>;
  if (item.link.startsWith("/"))
    return (
      <Link to={item.link} className="block">
        {inner}
      </Link>
    );
  return (
    <a
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      className="block"
    >
      {inner}
    </a>
  );
}
